
import _ from 'lodash'
import PropTypes from 'prop-types'
import { useCallback, useState } from 'react'
import { connect } from 'react-redux'
import mapToFhir from 'shared_src/mappings/related_person/to_fhir'
import { mapToForm } from 'shared_src/mappings/related_person/to_form'

import { withLegacyRouter } from '../../../../../decorators'
import { RelationshipCodes } from '../../../../../lib/Codes'
import { titleCase } from '../../../../../lib/Form'
import { phonesToFhir } from '../../../../../lib/Proxy/lib/phone'
import { IGAO } from '../../../../igao'
import * as Card from '../../../../shared/card'
import CrudTable from '../../../../shared/CrudTable'
import { setListEmpty } from '../../../../shared/CrudTable/actions'
import ConfirmationDeleteDialog from '../../../../shared/DeleteDialog/ConfirmationDeleteDialog'
import { Checkbox } from '../../../../shared/NarusMaterial'

import Member from './components/member'

const checkComponent = () => <i className='fa fa-check' />
const starComponent = () => <i className='fa fa-star' />

const getRelationship = value => {
  const foundCode = RelationshipCodes.find(code => code.value === value)

  return foundCode ? titleCase(foundCode.display) : ''
}

const mkSpec = patientId => ({
  type: 'supportMember',
  form: Member,
  urls: {
    get: () => `/patients/${patientId}/lists/relatedpersons`,
    create: () => `/patients/${patientId}/lists/relatedpersons`,
    update: entry =>
      `/patients/${patientId}/lists/relatedpersons/${entry.id}`,
    delete: entry =>
      `/patients/${patientId}/lists/relatedpersons/${entry.id}`
  },
  mapFns: {
    toForm: x => mapToForm(x),
    toFhir: entry => {
      entry.emails = _.compact(entry.emails)
      entry.phones = phonesToFhir(entry.phones)
      return mapToFhir(entry, patientId)
    }
  },
  table: [
    {
      title: 'Last Name',
      displayFn: x => x.lastName
    },
    {
      title: 'First Name',
      displayFn: x => x.firstName
    },
    {
      title: 'Relationship',
      displayFn: x => getRelationship(x.relationship)
    },
    {
      title: 'Caregiver',
      displayFn: x => {
        if (x.narus && x.narus.caregiver) {
          if (x.narus.caregiverPrimary) {
            return starComponent()
          }
          return checkComponent()
        }
        return ''
      },
      sortFn: x => {
        if (x.narus && x.narus.caregiver) {
          if (x.narus.caregiverPrimary) {
            return 1
          }
          return 2
        }
        return 3
      }
    },
    {
      title: 'Emergency',
      displayFn: x => {
        if (x.narus && x.narus.emergency) {
          if (x.narus.emergencyPrimary) {
            return starComponent()
          }
          return checkComponent()
        }
        return ''
      },
      sortFn: x => {
        if (x.narus && x.narus.emergency) {
          if (x.narus.emergencyPrimary) {
            return 1
          }
          return 2
        }
        return 3
      }
    },
    {
      title: 'POA',
      displayFn: ({ narus }) => {
        if (narus && narus.poa) {
          return checkComponent()
        }
        return null
      },
      sortFn: x => (x.narus && x.narus.poa ? 1 : 0)
    },
    {
      title: 'Permission',
      displayFn: ({ narus }) => {
        if (narus && narus.permission) {
          return checkComponent()
        }
        return null
      },
      sortFn: x => (x.narus && x.narus.permission ? 1 : 0)
    },
    {
      title: 'Mobile',
      displayFn: x => {
        if (x.mobile) {
          return checkComponent()
        }
        return ''
      },
      sortFn: x => {
        if (x.narus) {
          return 1
        }
        return 2
      }
    },

    {
      title: 'Link',
      displayFn: x =>
        (x.link
          ? (
            <a
              href={`/patient/${x.link}/patientFolder`}
            >
              {`${x.lastName}, ${x.firstName}`}
            </a>
          )
          : (
            ''
          ))
    }
  ]
})

function SupportNetwork (props) {
  const {
    params,
    isListEmpty,
    entries,
    patientId,
    loading,
    dispatch,
    clientName
  } = props
  const ptId = params ? params.id : patientId
  const spec = mkSpec(ptId)

  let [ key, setKey ] = useState(0)

  let submitCallback = useCallback(() => {
    setKey(key => key + 1)
  }, [])

  return (
    <div className='main' >
      <Card.Card expandable>
        <Card.Header>
          <h2>Family | Support Members</h2>
          <Checkbox
            label='Patient reports no family/support members'
            value={isListEmpty}
            disabled={loading || entries.length > 0}
            onChange={val => {
              dispatch(
                setListEmpty(
                  'supportMembers',
                  `/patients/${patientId}/lists/relatedpersons`,
                  clientName,
                  val,
                  spec.urls,
                  spec.mapFns
                )
              )
            }}
          />
        </Card.Header>
        <Card.ExpandableWrapper
          style={{ padding: '0 1rem 1rem 1rem' }}
        >
          <CrudTable
            labelButton='Member'
            labelNoEntries='Family and Support Members'
            urlFns={spec.urls}
            entryType={spec.type}
            entrySet={patientId}
            tableDefinition={spec.table}
            mapFns={spec.mapFns}
            form={spec.form}
            disabled={isListEmpty}
            deleteDialog={ConfirmationDeleteDialog}
            callbackFn={submitCallback}
          />
        </Card.ExpandableWrapper>
      </Card.Card>

      <IGAO
        key={key}
        path='family_support'
      />
    </div>
  )
}

SupportNetwork.contextTypes = {
  dispatch: PropTypes.instanceOf(Object)
}

const mapStateToProps = redux => {
  const patientId = redux?.globalVars?.params?.id
  const isListEmpty = _.get(
    redux,
    'crudTable.supportMembers.additionalData.empty',
    false
  )
  return {
    isListEmpty,
    entries: _.get(redux, 'crudTable.supportMembers.entries', []),
    loading: _.get(redux, 'crudTable.supportMembers.getNewEntries', true),
    patientId,
    clientName: redux.globalVars.params.clientName
  }
}

export default connect(mapStateToProps)(withLegacyRouter(SupportNetwork))
